/**
 * iter92 — Lightweight admin-only error reporter.
 *
 * Hooks `window.error` + `window.unhandledrejection` and forwards
 * anything caught to `POST /api/admin/client-errors`. Deliberately
 * scoped to `/admin/*` routes so we don't spam the collection with
 * third-party script noise from the storefront (chat widgets,
 * analytics pixels, etc.).
 *
 * Also exposes `reportClientError(err, kind)` for imperative reports
 * from try/catch blocks that already handle failure gracefully but
 * want to leave a breadcrumb.
 */
import { apiClient } from "./api";

const IS_ADMIN_ROUTE = () =>
  typeof window !== "undefined" &&
  (window.location.pathname === "/admin" ||
    window.location.pathname.startsWith("/admin/"));

// Simple in-memory throttle: don't send the same message more than once
// every 30 seconds from a single tab (backend also de-dups by IP+message).
const _lastSentAt = new Map();
const THROTTLE_MS = 30 * 1000;

async function _post(payload) {
  try {
    // Use apiClient so REACT_APP_BACKEND_URL is honoured, but SKIP the
    // response interceptor by using a fresh axios call to avoid
    // infinite loops if the reporter itself errors.
    await apiClient.post("/admin/client-errors", payload, {
      // Explicitly clear Auth so we don't accidentally attach a stale
      // header to an anonymous ingest endpoint.
      headers: { "X-Client-Report": "1" },
      timeout: 5000,
    });
  } catch {
    // Reporting failures must NEVER re-throw or the page loops forever.
  }
}

function _shouldSend(message) {
  const now = Date.now();
  const last = _lastSentAt.get(message) || 0;
  if (now - last < THROTTLE_MS) return false;
  _lastSentAt.set(message, now);
  return true;
}

function _safeStack(err) {
  if (!err) return null;
  if (typeof err === "string") return null;
  return (err.stack || "").slice(0, 8000) || null;
}

function _safeMessage(err, fallback) {
  if (!err) return fallback || "Unknown error";
  if (typeof err === "string") return err.slice(0, 2000);
  return String(err.message || err.name || fallback || err).slice(0, 2000);
}

let _installed = false;

/**
 * Install the global listeners. Idempotent — safe to call from useEffect
 * without worrying about double-registration on React StrictMode remounts.
 */
export function installClientErrorReporter() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;

  window.addEventListener("error", (evt) => {
    if (!IS_ADMIN_ROUTE()) return;
    const message = _safeMessage(evt.error || evt.message, evt.message);
    if (!_shouldSend(`error:${message}`)) return;
    _post({
      message,
      stack: _safeStack(evt.error),
      source: (evt.filename || "").slice(0, 500) || null,
      line: evt.lineno ?? null,
      column: evt.colno ?? null,
      kind: "error",
      route: window.location.pathname,
      user_agent: (navigator.userAgent || "").slice(0, 500),
      build_id: process.env.REACT_APP_BUILD_ID || null,
    });
  });

  window.addEventListener("unhandledrejection", (evt) => {
    if (!IS_ADMIN_ROUTE()) return;
    const reason = evt.reason;
    const message = _safeMessage(reason, "Unhandled promise rejection");
    if (!_shouldSend(`rejection:${message}`)) return;
    _post({
      message,
      stack: _safeStack(reason),
      source: null,
      line: null,
      column: null,
      kind: "unhandledrejection",
      route: window.location.pathname,
      user_agent: (navigator.userAgent || "").slice(0, 500),
      build_id: process.env.REACT_APP_BUILD_ID || null,
    });
  });
}

/**
 * Manual report — call from a try/catch you already handle when you want
 * a breadcrumb in the admin log ("this failed but the UI recovered").
 */
export function reportClientError(err, { kind = "manual", context } = {}) {
  if (!IS_ADMIN_ROUTE()) return;
  const message = _safeMessage(err, context) + (context ? ` [${context}]` : "");
  if (!_shouldSend(`${kind}:${message}`)) return;
  _post({
    message,
    stack: _safeStack(err),
    source: null,
    line: null,
    column: null,
    kind,
    route: window.location.pathname,
    user_agent: (navigator.userAgent || "").slice(0, 500),
    build_id: process.env.REACT_APP_BUILD_ID || null,
  });
}
